let audioCtx: AudioContext | null = null;

/**
 * Checks if sound remains enabled in user settings.
 */
export function isSoundActive(): boolean {
  try {
    return localStorage.getItem('priyofy_sound') !== 'false';
  } catch {
    return true;
  }
}

/**
 * Checks if haptic feedback remains enabled in user settings.
 */
export function isHapticActive(): boolean {
  try {
    return localStorage.getItem('priyofy_haptic') !== 'false';
  } catch {
    return true;
  }
}

/**
 * Returns the configured haptic intensity multiplier (50 to 250, default 100)
 */
export function getHapticStrength(): number {
  try {
    const val = localStorage.getItem('priyofy_haptic_strength');
    return val ? parseInt(val, 10): 100;
  } catch {
    return 100;
  }
}

/**
 * Returns the configured sound volume multiplier (10 to 200, default 100)
 */
export function getSoundVolume(): number {
  try {
    const val = localStorage.getItem('priyofy_sound_volume');
    return val ? parseInt(val, 10): 100;
  } catch {
    return 100;
  }
}

/**
 * Triggers a short haptic feedback pulse, scaled by user preferences.
 */
export function triggerHaptic(type: 'light' | 'success' | 'heavy' | 'pop' = 'light') {
  if (!isHapticActive()) return;
  if (typeof navigator !== 'undefined' && navigator.vibrate) {
    try {
      const scale = getHapticStrength() / 100;
      if (type === 'success') {
        const p1 = Math.max(15, Math.round(40 * scale));
        const gap = Math.max(15, Math.round(30 * scale));
        const p2 = Math.max(15, Math.round(40 * scale));
        navigator.vibrate([p1, gap, p2]);
      } else if (type === 'heavy') {
        navigator.vibrate(Math.max(20, Math.round(65 * scale)));
      } else if (type === 'pop') {
        navigator.vibrate(Math.max(10, Math.round(20 * scale)));
      } else {
        // Crisp, fast tap vibrate for a native app feel on mobile
        navigator.vibrate(Math.max(15, Math.round(30 * scale)));
      }
    } catch {
      // Browsers may block vibration until clicked, which is fine
    }
  }
}

/**
 * Generates dynamic synthesizer sound feedback using the Web Audio API with configurable volume.
 */
export function playSound(type: 'tap' | 'success' | 'remove' | 'pop' = 'tap') {
  if (!isSoundActive()) return;
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;

    if (!audioCtx) {
      audioCtx = new AudioContextClass();
    }
    
    // Resume context if suspended (browser security constraint)
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }

    const osc = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();

    osc.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    const now = audioCtx.currentTime;
    // Volume scaler (Convert 10-200 to 0.1 - 2.0 multiplier)
    const volScale = getSoundVolume() / 100;

    if (type === 'success') {
      // Upward, bright double alert sound
      osc.type = 'sine';
      osc.frequency.setValueAtTime(580, now);
      osc.frequency.exponentialRampToValueAtTime(980, now + 0.15);
      gainNode.gain.setValueAtTime(0.45 * volScale, now);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
      osc.start(now);
      osc.stop(now + 0.15);
    } else if (type === 'remove') {
      // Low tone sweep
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(280, now);
      osc.frequency.exponentialRampToValueAtTime(140, now + 0.18);
      gainNode.gain.setValueAtTime(0.40 * volScale, now);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
      osc.start(now);
      osc.stop(now + 0.18);
    } else if (type === 'pop') {
      // Soft organic bubble popup sounds
      osc.type = 'sine';
      osc.frequency.setValueAtTime(500, now);
      osc.frequency.exponentialRampToValueAtTime(320, now + 0.08);
      gainNode.gain.setValueAtTime(0.35 * volScale, now);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
      osc.start(now);
      osc.stop(now + 0.08);
    } else {
      // Crisp, high-end snappy click sound for standard buttons
      osc.type = 'sine';
      osc.frequency.setValueAtTime(600, now);
      osc.frequency.exponentialRampToValueAtTime(750, now + 0.05);
      gainNode.gain.setValueAtTime(0.35 * volScale, now);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
      osc.start(now);
      osc.stop(now + 0.05);
    }
  } catch (error) {
    console.warn('Native sound synthesis error:', error);
  }
}

/**
 * Triggers both light haptic and tap sound instantly.
 */
export function triggerTapFeedback() {
  triggerHaptic('light');
  playSound('tap');
}

/**
 * Triggers success haptic and sound.
 */
export function triggerSuccessFeedback() {
  triggerHaptic('success');
  playSound('success');
}

/**
 * Triggers remove/pop haptic and sound.
 */
export function triggerRemoveFeedback() {
  triggerHaptic('heavy');
  playSound('remove');
}

/**
 * Triggers soft popup haptic and sound.
 */
export function triggerPopFeedback() {
  triggerHaptic('pop');
  playSound('pop');
}
